/**
 * Application grants — the consent screen's and settings screen's API.
 *
 * Two audiences, and the split matters:
 *
 * - `describeAuthorizationRequest` / `approveAuthorizationRequest` back the
 *   consent screen. They are the only place a third party's request is turned
 *   into something a user is shown, and everything rendered comes from the
 *   RESPONSE, never from the query string the application controls.
 * - `listGrants` / `revokeGrant` back account settings, which is where the user
 *   ends an application's access without signing out anywhere else.
 *
 * A grant's access token is never handled here. It is delivered to the
 * application through the redirect and exchanged by the application itself; the
 * browser never holds it, and neither does this code.
 */

import { apiClient } from './apiClient';
import { buildCloudApiUrl } from './urlResolver';

/**
 * Grant endpoints live on Cloud Run and NOWHERE else.
 *
 * That is the security design, not an accident: Cloud Run is the authorization
 * server, and the desktop backend listens on the loopback where any web page in
 * the user's browser can post to it — so it mints nothing and does not mount
 * these routes at all.
 *
 * `apiClient`'s baseURL is the LOCAL backend in Electron, so calling these with
 * a relative path 404s on the desktop: the consent screen and the settings list
 * were both broken there. `buildCloudApiUrl` is the existing helper for exactly
 * this class of endpoint (jackpot, billing, payments), and axios ignores
 * `baseURL` when handed an absolute URL, so the auth interceptor still applies.
 */
const grantsUrl = (path: string): string => buildCloudApiUrl(`/api/v1/grants${path}`);

/** What the consent screen renders about the asking application. */
export interface GrantApplication {
  client_id: string;
  /** From the server's registration — an application cannot name itself here. */
  display_name: string;
  publisher: string;
}

/** One capability the application is asking for, in plain language. */
export interface GrantScopeDescription {
  scope: string;
  display_name: string;
  summary: string;
  /** True when approving this lets the application spend the user's credit. */
  bills_the_user: boolean;
}

export interface AuthorizationRequestDescription {
  application: GrantApplication;
  scopes: GrantScopeDescription[];
  redirect_uri: string;
}

/** An application the account has authorized, as shown in settings. */
export interface AccountGrant {
  grant_id: string;
  client_id: string;
  display_name: string;
  publisher: string | null;
  scopes: string[];
  created_at: number | null;
  last_used_at: number | null;
}

/**
 * The parameters an application put in the consent URL.
 *
 * Every field is attacker-controlled: it arrives in a query string that anybody
 * can construct. Nothing here is rendered directly — it is sent to the server,
 * which validates it and answers with what may be shown.
 */
export interface AuthorizationRequestParams {
  client_id: string;
  redirect_uri: string;
  scope: string;
  state: string;
  code_challenge: string;
  code_challenge_method: string;
  installation_key: Record<string, string>;
}

/** Read the request out of the current URL, or null when it is incomplete. */
export function parseAuthorizationRequest(search: string): AuthorizationRequestParams | null {
  const params = new URLSearchParams(search);
  const required = [
    'client_id',
    'redirect_uri',
    'scope',
    'state',
    'code_challenge',
    'code_challenge_method',
    'installation_key',
  ];
  if (required.some((name) => !params.get(name))) {
    return null;
  }

  let installationKey: Record<string, string>;
  try {
    installationKey = JSON.parse(atob(params.get('installation_key') as string));
  } catch {
    return null;
  }

  return {
    client_id: params.get('client_id') as string,
    redirect_uri: params.get('redirect_uri') as string,
    scope: params.get('scope') as string,
    state: params.get('state') as string,
    code_challenge: params.get('code_challenge') as string,
    code_challenge_method: params.get('code_challenge_method') as string,
    installation_key: installationKey,
  };
}

/**
 * Ask the server to validate a proposed grant and describe it.
 *
 * Nothing is rendered until this succeeds, which is what stops an unregistered
 * application from ever reaching a screen carrying Asgard's branding.
 */
export async function describeAuthorizationRequest(
  request: AuthorizationRequestParams
): Promise<AuthorizationRequestDescription> {
  const response = await apiClient.post(grantsUrl('/authorization-request'), request);
  return response.data.data as AuthorizationRequestDescription;
}

/**
 * Approve the request and get the URL the browser should be sent to.
 *
 * Requires the account's own session — the interceptor attaches it. The server
 * re-validates every parameter before minting, so approving cannot widen what
 * was described.
 */
export async function approveAuthorizationRequest(
  request: AuthorizationRequestParams
): Promise<string> {
  const response = await apiClient.post(grantsUrl('/consent'), request);
  return response.data.data.redirect_to as string;
}

/** Every application this account has authorized. */
export async function listGrants(): Promise<AccountGrant[]> {
  const response = await apiClient.get(grantsUrl(''));
  return (response.data.data.grants ?? []) as AccountGrant[];
}

/** End one application's access. The account's own sessions are untouched. */
export async function revokeGrant(grantId: string): Promise<void> {
  await apiClient.delete(grantsUrl(`/${encodeURIComponent(grantId)}`));
}
